// src/services/matchingService.js
import api from "../api";
import { handleApiError } from "../utils/errorHandler";
import { notifyJourneyChanged } from "./companyJourneyService";

// A new swipe changes which listings belong on the discover deck, but the deck
// is served by GET /listings/matches. That is a different route root than the
// /matching/swipe we POST, so api.js's automatic same-root invalidation never
// busts it (and /provider/bootstrap re-seeds it). Clear both AFTER the POST
// succeeds, so the next deck fetch recomputes without the listing we just acted
// on. Cache-clear only: it can only force a fresh fetch, never change behavior.
const refreshDeckCaches = () => {
  if (typeof api.invalidateCache === "function") {
    api.invalidateCache("/listings/matches");
    api.invalidateCache("/provider/bootstrap");
  }
};

export const matchingService = {
  // Provider swipes on a listing
  providerSwipe: async (listingId, direction, options = {}) => {
    if (!listingId) {
      throw new Error("listingId is required");
    }
    if (!direction) {
      throw new Error("direction is required");
    }
    try {
      const response = await api.post("/matching/swipe", {
        listingId,
        direction,
        ...(options.clientActionId
          ? { clientActionId: options.clientActionId }
          : {}),
        ...(options.fitAcknowledged === true ? { fitAcknowledged: true } : {}),
      });
      refreshDeckCaches();
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to record provider swipe");
    }
  },

  // Provider expresses interest AND submits availability + pending questions
  // in one call. Backend creates the swipe, advances state to INTERVIEW, and
  // persists interviewRequest. Feature-gated behind INTERVIEW_REQUEST_FORM_ENABLED
  // server-side; frontend should only call this when the same flag is ON.
  //
  // payload shape:
  //   { listingId, slots: [{ date, startTime, endTime, timezone }], questions: [],
  //     introConsent?: boolean }
  expressInterestWithInterviewRequest: async (payload) => {
    if (!payload?.listingId) {
      throw new Error("listingId is required");
    }
    if (!Array.isArray(payload.slots) || payload.slots.length === 0) {
      throw new Error("At least one availability slot is required");
    }
    try {
      const response = await api.post(
        "/matching/swipe-with-interview-request",
        {
          listingId: payload.listingId,
          slots: payload.slots,
          questions: payload.questions || [],
          // Recruiter-originated Provider Pipeline rows carry their source Swipe
          // so the backend can preserve the exact recruiter seat atomically.
          ...(payload.sourceInterestSwipeId
            ? { sourceInterestSwipeId: payload.sourceInterestSwipeId }
            : {}),
          // Only forwarded when the caller stated a preference. The backend
          // treats an absent introConsent as consent, so a literal `false` has
          // to survive the wire or an explicit decline is silently ignored.
          ...(typeof payload.introConsent === "boolean"
            ? { introConsent: payload.introConsent }
            : {}),
          ...(payload.clientActionId
            ? { clientActionId: payload.clientActionId }
            : {}),
          // The fit confirmation was shown and accepted before the form opened.
          ...(payload.fitAcknowledged === true ? { fitAcknowledged: true } : {}),
        },
      );
      refreshDeckCaches();
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to submit interview request");
    }
  },

  // "Not right now, just connect us" path. Right-swipes the listing without
  // an interview request: the backend creates the Swipe in the pre-interview
  // connected (pending) state, persists any opening questions, and records
  // the OPENING_QUESTIONS event. Same shape as a plain right-swipe plus the
  // optional openingQuestions array.
  //
  // payload shape: { listingId, openingQuestions?: string[] }
  connectWithoutInterview: async (payload) => {
    if (!payload?.listingId) {
      throw new Error("listingId is required");
    }
    try {
      const response = await api.post("/matching/swipe", {
        listingId: payload.listingId,
        direction: "right",
        openingQuestions: payload.openingQuestions || [],
        ...(payload.clientActionId
          ? { clientActionId: payload.clientActionId }
          : {}),
        ...(payload.fitAcknowledged === true ? { fitAcknowledged: true } : {}),
      });
      refreshDeckCaches();
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to connect with this group");
    }
  },

  // Provider answers a recruiter-originated interest using the inbound group
  // Swipe as the authority and pair anchor. Unlike the generic listing swipe,
  // this preserves the exact recruiter seat that initiated the outreach in a
  // multi-seat organization.
  //
  // payload shape: direction 'right'|'left', options { openingQuestions?: [] }
  respondToInboundInterest: async (inboundSwipeId, direction, options = {}) => {
    if (!inboundSwipeId) {
      throw new Error("inboundSwipeId is required");
    }
    if (direction !== "right" && direction !== "left") {
      throw new Error("direction must be right or left");
    }
    try {
      const response = await api.post(
        `/matching/inbound-interest/${inboundSwipeId}/respond`,
        {
          direction,
          ...(direction === "right"
            ? { openingQuestions: options.openingQuestions || [] }
            : {}),
          ...(options.clientActionId
            ? { clientActionId: options.clientActionId }
            : {}),
          ...(direction === "right" && options.fitAcknowledged === true
            ? { fitAcknowledged: true }
            : {}),
        },
      );
      refreshDeckCaches();
      if (typeof api.invalidateCache === "function") {
        api.invalidateCache("/provider/inbound-interest");
        api.invalidateCache("/pipeline/provider");
      }
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to respond to recruiter interest");
    }
  },

  // Attaches an interview request to an existing swipe (the in-negotiation
  // "Request an interview" entry point). The backend writes interviewRequest
  // onto the swipe, transitions pending|negotiating -> interview, and records
  // the INTERVIEW_REQUEST_DETAILS event. Rejected server-side if the swipe
  // already has an interviewRequest.
  //
  // payload shape: { slots: [{ date, startTime, endTime, timezone }] }
  requestInterviewForSwipe: async (swipeId, payload) => {
    if (!swipeId) {
      throw new Error("swipeId is required");
    }
    if (!Array.isArray(payload?.slots) || payload.slots.length === 0) {
      throw new Error("At least one availability slot is required");
    }
    try {
      const response = await api.post(
        `/matching/swipes/${swipeId}/interview-request`,
        {
          slots: payload.slots,
          ...(typeof payload.introConsent === "boolean"
            ? { introConsent: payload.introConsent }
            : {}),
        },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to submit interview request");
    }
  },

  // Group accepts one of the provider's proposed slots. The free connection
  // marker must be applied atomically before the slot is confirmed.
  acceptInterviewSlot: async (swipeId, slotIndex) => {
    if (!swipeId) throw new Error("swipeId is required");
    if (typeof slotIndex !== "number") {
      throw new Error("slotIndex is required");
    }
    try {
      const response = await api.post(
        `/negotiations/state/${swipeId}/accept-interview-slot`,
        { slotIndex },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to accept interview slot");
    }
  },

  // Provider accepts the company's counter-proposed interview time. One call
  // confirms the interview (no payment — the company already recorded its
  // free connection when it proposed the counter).
  acceptCounterProposal: async (swipeId) => {
    if (!swipeId) throw new Error("swipeId is required");
    try {
      const response = await api.post(
        `/negotiations/state/${swipeId}/accept-counter-proposal`,
      );
      return response.data;
    } catch (error) {
      throw handleApiError(
        error,
        "Failed to accept the proposed interview time",
      );
    }
  },

  // Group declines a pending interview request. Transitions the swipe
  // interview -> declined via the backend state machine.
  declineInterviewRequest: async (swipeId, reason) => {
    if (!swipeId) throw new Error("swipeId is required");
    try {
      const response = await api.post(
        `/negotiations/state/${swipeId}/decline-interview-request`,
        reason ? { reason } : {},
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to decline interview request");
    }
  },

  // Group swipes on a provider
  groupSwipe: async (providerProfileId, direction, listingId, options = {}) => {
    if (!listingId) {
      throw new Error("listingId is required for group swipes");
    }
    if (!providerProfileId) {
      throw new Error("providerProfileId is required");
    }
    if (!direction) {
      throw new Error("direction is required");
    }

    try {
      const response = await api.post("/matching/group-swipe", {
        providerProfileId,
        direction,
        listingId,
        ...(options.clientActionId
          ? { clientActionId: options.clientActionId }
          : {}),
        ...(options.fitAcknowledged === true ? { fitAcknowledged: true } : {}),
      });
      // First look, first match and first conversation are all read off group
      // swipes, so the board is stale the moment one lands. Left swipes count
      // too: first look has no direction filter, because a pass still means the
      // deck was opened. Guarded separately from the request above: this sits
      // inside the same try block, so an unguarded throw here would be caught
      // by the catch below and reported as a failed swipe that actually landed.
      try {
        notifyJourneyChanged();
      } catch {
        /* the journey board is decoration; it must never fail the swipe it followed */
      }
      return response.data;
    } catch (error) {
      throw handleApiError(
        error,
        `Failed swipe for provider ${providerProfileId}`,
      );
    }
  },

  // Get matches for a provider
  getProviderMatches: async () => {
    try {
      const response = await api.get("/matching/provider-matches");

      // For each match, check if there's a declined status from the group side
      const enhancedMatches = await Promise.all(
        response.data.map(async (match) => {
          try {
            // Check if both IDs are available before calling getDetailedMatchStatus
            if (match.listing?._id && match.providerProfile?._id) {
              // Get detailed match status including both group and provider swipes
              const detailedMatch =
                await matchingService.getDetailedMatchStatus(
                  match.listing._id,
                  match.providerProfile._id,
                );

              return {
                ...match,
                groupSwipeStatus: detailedMatch.groupSwipeStatus,
                declinedBy: detailedMatch.declinedBy,
              };
            } else {
              console.warn(
                "Skipping detailed status check for match - missing listing ID or provider profile ID",
                {
                  listingId: match.listing?._id,
                  providerProfileId: match.providerProfile?._id,
                },
              );
              console.warn(
                `Detailed match status for match ${JSON.stringify(match)}:`,
              );
              return match;
            }
          } catch (err) {
            console.error("Error fetching detailed match status:", err);
            return match;
          }
        }),
      );

      // Add legacy alias fields for backward compatibility
      const normalizedMatches = enhancedMatches.map((m) => ({
        ...m,
        listingId: m.listing || m.listingId,
        providerProfileId: m.providerProfile || m.providerProfileId,
      }));
      return normalizedMatches;
    } catch (error) {
      throw handleApiError(error, "Failed to fetch provider matches");
    }
  },

  // Get ALL swipes for a provider (not just matches) - for Matches view with filters
  getProviderSwipes: async () => {
    try {
      const response = await api.get("/matching/provider-swipes");
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to fetch provider swipes");
    }
  },

  // Get detailed status for a specific match (both provider and group swipe records)
  getDetailedMatchStatus: async (listingId, providerProfileId) => {
    if (!listingId || !providerProfileId) {
      throw new Error("Both listingId and providerProfileId are required");
    }
    try {
      // First try to get group-initiated swipe status for this listing/provider
      const groupSwipeResponse = await api.get(
        `/matching/swipe?listingId=${listingId}&providerProfileId=${providerProfileId}&initiatedBy=group`,
      );
      const groupSwipeData = groupSwipeResponse.data;

      // Check if the group declined this provider
      let declinedBy = null;
      if (
        groupSwipeData &&
        (groupSwipeData.status === "declined" ||
          groupSwipeData.direction === "left")
      ) {
        declinedBy = "group";
      }

      return {
        groupSwipe: groupSwipeData,
        initiatedBy: groupSwipeData?.initiatedBy || null,
        groupSwipeStatus: groupSwipeData?.status || null,
        declinedBy,
      };
    } catch (error) {
      // If the backend API is not yet implemented, we'll simulate it on the frontend
      console.warn(
        "Detailed match status API not available, falling back to available data",
      );
      return {
        groupSwipe: null,
        initiatedBy: null,
        groupSwipeStatus: null,
        declinedBy: null,
      };
    }
  },

  // Update match status
  updateMatchStatus: async (swipeId, status) => {
    if (!swipeId) {
      throw new Error("swipeId is required");
    }
    try {
      const response = await api.patch(`/matching/match-status/${swipeId}`, {
        status,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to update match status");
    }
  },

  // Update swipe direction (for "Interested Again" functionality)
  updateSwipeDirection: async (swipeId, direction, options = {}) => {
    if (!swipeId) {
      throw new Error("swipeId is required");
    }
    if (!direction) {
      throw new Error("direction is required");
    }
    try {
      const response = await api.patch(`/matching/swipe-direction/${swipeId}`, {
        direction,
        ...(options.clientActionId
          ? { clientActionId: options.clientActionId }
          : {}),
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to update swipe direction");
    }
  },

  // Add notes to a match
  addMatchNotes: async (swipeId, notes) => {
    if (!swipeId) {
      throw new Error("swipeId is required");
    }
    try {
      const response = await api.patch(`/matching/match-notes/${swipeId}`, {
        notes,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to add match notes");
    }
  },

  // Mark a match as viewed by the provider
  markMatchViewed: async (swipeId) => {
    if (!swipeId) {
      throw new Error("swipeId is required");
    }
    try {
      const response = await api.patch(`/matching/mark-viewed/${swipeId}`, {
        viewedByProvider: true,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to mark match as viewed");
    }
  },

  // Browse providers for groups (optionally scoped to a listing)
  // sort: optional 'availability' to rank by schedule overlap (M2 backend flag required)
  browseProviders: async (
    page = 1,
    limit = 10,
    excludeSwiped = true,
    listingId,
    sort,
  ) => {
    try {
      // Build query parameters, including listing filter if provided
      const params = { page, limit, exclude_swiped: excludeSwiped };
      if (listingId) params.listingId = listingId;
      if (sort && sort !== "default") params.sort = sort;
      // Use the 'available' route on provider controller
      const response = await api.get("/provider/available", { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, "Failed to browse providers");
    }
  },
};

export default matchingService;
